// src/pages/fleet/admin/UsersPage.tsx — Administration → Users.
// New accounts are Fleet-only users whose access comes from the roles ticked here.
// Super Admin accounts always have full access: they are listed, but cannot be deleted
// and only another Super Admin may edit them.
import React, { useEffect, useState } from 'react';
import MasterTable, { FormValues, Pill } from '../master/MasterTable';
import type { FleetRole, User } from '../../../types';
import { getAdminUsers, getRoles, createUser, updateUser, deleteUser } from '../../../services/fleetApi';
import { useAuth } from '../../../store/AuthContext';
import { apiError } from '../../../utils/errors';

type UserRow = User & { status: 'Active' | 'Inactive'; roleNames: string };

const isSuper = (u: { role?: string } | null | undefined) => u?.role === 'SUPER_ADMIN';

function ago(d: string | null | undefined): string {
  if (!d) return 'Never';
  const m = Math.round((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.round(m / 60)} hr ago`;
  return `${Math.round(m / 1440)} day ago`;
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState<UserRow[]>([]);
  const [roles, setRoles] = useState<FleetRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');

  const load = () => Promise.all([getAdminUsers(), getRoles()])
    .then(([u, r]) => {
      setRows((u.data.users || []).map((x) => ({
        ...x,
        status: x.isActive === false ? 'Inactive' : 'Active',
        roleNames: isSuper(x) ? 'Super Admin' : (x.fleetRoles || []).map((f) => f.name).join(', '),
      })));
      setRoles(r.data.roles || []);
      setLoadErr('');
    })
    .catch((e) => setLoadErr(apiError(e, 'Could not load users')))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const onSave = async (form: FormValues, existing: UserRow | null) => {
    const body = {
      name: String(form.name ?? '').trim(),
      email: String(form.email ?? '').trim(),
      password: String(form.password ?? '') || undefined,
      roleIds: existing && isSuper(existing) ? undefined : ((form.roleIds as string[]) || []),
      isActive: existing ? form.isActive !== false : undefined,
    };
    if (existing) await updateUser(existing.id, body);
    else await createUser(body);
    await load();
  };
  const onDelete = async (r: UserRow) => {
    const res = await deleteUser(r.id);
    await load();
    if (res.data.deactivated) throw new Error(`${r.name} has linked records, so the account was deactivated instead of deleted.`);
  };

  const validate = (f: FormValues, existing: UserRow | null): string | null => {
    const pw = String(f.password ?? '');
    if (!existing && !pw) return 'Enter a password';
    if (pw && pw.length < 8) return 'Password must be at least 8 characters';
    if (pw !== String(f.confirmPassword ?? '')) return 'Passwords do not match';
    return null;
  };

  const superHidden = (_: FormValues, existing: unknown) => isSuper(existing as UserRow | null);
  const selfOrSuper = (_: FormValues, existing: unknown) => {
    const r = existing as UserRow | null;
    return !!r && (isSuper(r) || r.id === me?.id);
  };

  return (
    <MasterTable<UserRow>
      icon="users" title="Users" addLabel="User"
      subtitle={loading ? 'Loading…' : loadErr || `${rows.length} accounts with Fleet access`}
      rows={rows}
      searchKeys={['name', 'email', 'roleNames']}
      filters={[{ key: 'status', label: 'Status', options: ['Active', 'Inactive'] }]}
      emptyText={loadErr || 'No users yet'}
      stats={[
        { label: 'Users', value: rows.length, tint: 'var(--fx-accent)' },
        { label: 'Active', value: rows.filter((r) => r.status === 'Active').length, tint: 'var(--fx-green)' },
        { label: 'Super Admins', value: rows.filter(isSuper).length, tint: 'var(--fx-orange)' },
      ]}
      columns={[
        { key: 'name', label: 'Name', render: (r) => (
          <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={avatar}>{(r.name || '?')[0].toUpperCase()}</span>
            <b>{r.name}</b>
            {r.id === me?.id && <span style={youTag}>You</span>}
          </span>
        ) },
        { key: 'email', label: 'Email' },
        { key: 'roleNames', label: 'Roles', render: (r) => (isSuper(r)
          ? <span style={{ ...badge, ...badgeSuper }}>Super Admin</span>
          : (r.fleetRoles || []).length
            ? <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{(r.fleetRoles || []).map((f) => <span key={f.id} style={badge}>{f.name}</span>)}</span>
            : <span style={{ color: 'var(--fx-orange)', fontWeight: 600, fontSize: 12 }}>No role</span>) },
        { key: 'status', label: 'Status', width: 100, render: (r) => <Pill on={r.status === 'Active'} /> },
        { key: 'lastLoginAt', label: 'Last Login', width: 120, render: (r) => ago(r.lastLoginAt), csvValue: (r) => r.lastLoginAt || '' },
      ]}
      fields={[
        { key: 'name', label: 'Name', required: true, placeholder: 'Full name' },
        { key: 'email', label: 'Email', type: 'email', required: true, placeholder: 'name@rfcl.co.in' },
        { key: 'password', label: 'Password', type: 'password', required: true, optionalOnEdit: true, half: true,
          placeholder: 'At least 8 characters' },
        { key: 'confirmPassword', label: 'Confirm Password', type: 'password', required: true, optionalOnEdit: true, half: true,
          placeholder: 'Type it again' },
        { key: 'roleIds', label: 'Role', type: 'checkboxes', required: true, default: [], hidden: superHidden,
          options: roles.map((r) => ({ value: r.id, label: r.name, hint: `${r.permissions.length} permission${r.permissions.length === 1 ? '' : 's'}` })) },
        { key: 'isActive', label: 'Status', type: 'toggle', editOnly: true, hidden: selfOrSuper },
      ]}
      toForm={(r) => ({ name: r.name, email: r.email, password: '', confirmPassword: '', roleIds: (r.fleetRoles || []).map((f) => f.id), isActive: r.isActive !== false })}
      validate={validate}
      drawerWidth={520}
      onSave={onSave}
      onDelete={onDelete}
      canEdit={(r) => !isSuper(r) || isSuper(me)}
      canDelete={(r) => !isSuper(r) && r.id !== me?.id}
      rowLabel={(r) => `${r.name} (${r.email})`}
    />
  );
}

const avatar: React.CSSProperties = {
  width: 26, height: 26, borderRadius: '50%', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 11.5, fontWeight: 700, color: '#fff', background: 'linear-gradient(180deg, #8E8EF5 0%, #5856D6 100%)',
};
const youTag: React.CSSProperties = { fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 5, background: 'var(--fx-accent-soft)', color: 'var(--fx-accent)' };
const badge: React.CSSProperties = { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: 'var(--fx-gray-soft)', color: 'var(--fx-text)', whiteSpace: 'nowrap' };
const badgeSuper: React.CSSProperties = { background: 'var(--fx-text)', color: 'var(--fx-surface)' };
